/**
 * Flag-column display rules for the trajectories table (TRA-963).
 *
 * A row's flag is its newest non-tag-only label — the verdict, the comment
 * a reviewer or the dream scan wrote, and the eval-PR stamp. The verdict
 * itself is read by the review page's shared helpers; what lives here is
 * the trajectories-specific part: which chip a row wears, how rows order
 * under the Flag column, and whether the run's verdict still agrees with
 * the eval built from it.
 */

import { parseVerdictSummary } from '@/components/review/flagSource'
import type { RunFlag, RunListItem } from '@/api/generated/model'

/**
 * The single chip a row's Flag cell wears.
 *
 * 'tagged' is the tag-only bookkeeping state (tags, no flag); 'none' is a
 * run nobody has touched. The others are the flag's review state — the
 * verdict rides alongside as its own chip, so a reviewed row does not
 * have to choose between saying "reviewed" and saying "good". 'held'
 * (TRA-1057) is a flag the triage stage parked over the nightly review
 * budget: real waiting work, just not in the queue yet.
 */
export type FlagState =
  | 'unreviewed'
  | 'held'
  | 'reviewed'
  | 'dismissed'
  | 'tagged'
  | 'none'

export function flagState(item: RunListItem): FlagState {
  if (item.flag !== null) return item.flag.review_status
  return item.tags.length > 0 ? 'tagged' : 'none'
}

// Waiting work first, then settled work, then rows carrying no judgment at
// all — the order someone working the page down wants.
const STATE_RANK: Record<FlagState, number> = {
  unreviewed: 0,
  held: 1, // waiting too, just parked — sorts with work, not with settled
  reviewed: 2,
  dismissed: 3,
  tagged: 4,
  none: 5,
}

export function flagRank(item: RunListItem): number {
  return STATE_RANK[flagState(item)]
}

/**
 * Whether the run's verdict now contradicts the eval built from it.
 *
 * `pr_approved` is what the label said when the eval-PR agent stamped it,
 * so the published regression test grades THAT outcome. A reviewer who
 * later flips the verdict from the trajectories page leaves a test in the
 * customer repo asserting the opposite, and nothing else would say so.
 *
 * Only a REVIEWED row can contradict anything. Dismissing forces
 * `approved` false without making a good/bad claim (the same reason
 * `rowVerdict` attributes no reviewer verdict to a dismissed row), so
 * without this guard every dismissal of a run whose eval shipped as good
 * would raise a contradiction nobody asserted.
 *
 * False whenever there is nothing to compare, which `pr_approved` null
 * already says. That covers more than "no PR": the agent stamps episodes
 * it DROPPED with a `pr_url` too, and no test was built from those, so a
 * changed verdict there contradicts nothing. Silence is the honest answer
 * — a guessed divergence sends someone to read a PR that says nothing
 * about their run.
 */
export function evalDiverged(flag: RunFlag): boolean {
  if (flag.review_status !== 'reviewed') return false
  if (flag.pr_url === null || flag.pr_approved === null) return false
  return flag.pr_approved !== flag.approved
}

/** What the published eval asserts, for the divergence warning's copy. */
export function evalVerdict(flag: RunFlag): 'good' | 'bad' {
  return flag.pr_approved === true ? 'good' : 'bad'
}

/**
 * The comment to show, with the dream scan's `[good]`/`[bad]` marker
 * stripped — that prefix is our encoding of the judge's lean and it
 * renders as the verdict chip, never as text the reviewer appears to have
 * written. Empty when the flag carries no prose (a bare verdict, or a
 * dismissal saved without one).
 */
export function flagComment(flag: RunFlag): string {
  return parseVerdictSummary(flag.notes).text.trim()
}
